import React, { useEffect } from 'react';
import { UseFormReturn, useForm, UseFormProps, FormProvider, FieldValues, Mode, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

type FormMethods<T extends FieldValues> = Omit<UseFormReturn<T>, 'handleSubmit'>;
type FormChildren<T extends FieldValues> = React.ReactNode | ((methods: FormMethods<T>) => React.ReactNode);

export interface FormProps<T extends FieldValues> extends Pick<UseFormProps<T>, 'defaultValues' | 'values' | 'resolver'> {
  children: FormChildren<T>;
  onSubmit?: (value: T) => void;
  validationSchema?: Yup.ObjectSchema<T>;
  mode?: Mode;
  onFormStateChange?: (isValid: boolean) => void;
  id?: string;
}

const Form = <T extends FieldValues>({
  children,
  defaultValues,
  onSubmit,
  validationSchema,
  mode = 'onBlur',
  values,
  resolver,
  onFormStateChange,
  ...props
}: FormProps<T>) => {
  const methods = useForm<T>({
    resolver: (resolver ?? (validationSchema && yupResolver(validationSchema))) as Resolver<T, any>,
    defaultValues,
    values,
    mode,
  });

  const { handleSubmit, formState } = methods;
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(formState.isValid);
    }
  }, [formState.isValid, onFormStateChange]);
  return (
    <FormProvider {...methods}>
      {/* Without an onSubmit the native submit would reload the page on Enter. */}
      <form onSubmit={onSubmit ? handleSubmit(onSubmit) : (event) => event.preventDefault()} {...props}>
        {typeof children === 'function' ? children(methods) : children}
      </form>
    </FormProvider>
  );
};

export default Form;
